// A save document: a game's state as named, versioned slices (the kernel's module slices are one slice; the clock,
// RNG, input or physics snapshot can be others), plus metadata a slot list can show without decoding the state.
// The game declares its slices and their versions in a schema; reading a document runs each slice's migrations
// (the kernel's `migrateData`), drops slices the build no longer knows, and lets a `legacy` hook lift saves written
// before the document format existed. A slice that can't be migrated throws, so an old save never loads
// half-converted. Suggestion: versioned save slices (engine/docs/research/suggestions/runtime-input-kernel.md).
import { FIRST_SLICE_VERSION, migrateData, type MigrationRun, type MigrationSteps } from "@rpg/kernel";

/** The document format tag. */
export const SAVE_DOC_FORMAT = "rpg.save@1" as const;

/** One slice in a document: its data version and its data. */
export interface SliceData {
  v: number;
  data: unknown;
}

/** What a slot list shows without decoding the state; the game fills it (place, playtime, a state hash...). */
export type SaveMeta = Record<string, string | number | boolean | null>;

/** A save document. */
export interface SaveDoc {
  format: typeof SAVE_DOC_FORMAT;
  /** The game's id (a document from another game is refused). */
  game: string;
  /** Wall-clock ms when written (sync compares these). */
  savedAt: number;
  slices: Record<string, SliceData>;
  meta: SaveMeta;
}

/** A slice's current version and the steps that bring older data to it. */
export interface SliceSchema {
  version: number;
  /** `migrations[n]` turns data at version n - 1 into version n. */
  migrations?: MigrationSteps;
  /** A slice the document must have (default true); an optional slice may be absent. */
  required?: boolean;
}

/** The game's save schema. */
export interface SaveSchemaSpec {
  game: string;
  slices: Record<string, SliceSchema>;
  /** Lift a value that is not a document (a save from before the format) into one, or return null to refuse it. */
  legacy?: (raw: unknown) => SaveDoc | null;
}

/** A document read and brought to the current versions. */
export interface ReadResult {
  doc: SaveDoc;
  /** The migrations that ran, in order. */
  migrated: MigrationRun[];
  /** Slices the document had that this build doesn't know (dropped). */
  dropped: string[];
  /** The document came through the legacy hook. */
  legacy: boolean;
}

/** A value that isn't a save this game can read (wrong format or game, a required slice missing). */
export class SaveFormatError extends Error {
  override name = "SaveFormatError";
}

/** A declared schema: make documents, read (and migrate) them. */
export interface SaveSchema {
  readonly spec: SaveSchemaSpec;
  /** A document at the current versions from slice data. */
  make(slices: Record<string, unknown>, meta?: SaveMeta, savedAt?: number): SaveDoc;
  /** Check and migrate a decoded value. Throws SaveFormatError, or the kernel's SaveMigrationError. */
  read(raw: unknown): ReadResult;
}

const isDoc = (v: unknown): v is SaveDoc => !!v && typeof v === "object" && (v as { format?: unknown }).format === SAVE_DOC_FORMAT;

/** Declare a game's save schema. */
export function defineSaveSchema(spec: SaveSchemaSpec): SaveSchema {
  for (const [id, s] of Object.entries(spec.slices)) {
    if (!Number.isInteger(s.version) || s.version < FIRST_SLICE_VERSION) throw new Error(`save schema: slice "${id}" version must be an integer ≥ 1`);
  }
  return {
    spec,
    make(slices, meta = {}, savedAt = Date.now()) {
      const out: Record<string, SliceData> = {};
      for (const [id, data] of Object.entries(slices)) {
        const s = spec.slices[id];
        if (!s) throw new Error(`save schema: unknown slice "${id}"`);
        out[id] = { v: s.version, data };
      }
      for (const [id, s] of Object.entries(spec.slices)) if (s.required !== false && !(id in out)) throw new Error(`save schema: slice "${id}" is required`);
      return { format: SAVE_DOC_FORMAT, game: spec.game, savedAt, slices: out, meta: { ...meta } };
    },
    read(raw) {
      let legacy = false;
      let doc: SaveDoc | null = isDoc(raw) ? raw : null;
      if (!doc) {
        doc = spec.legacy?.(raw) ?? null;
        legacy = true;
        if (!doc || !isDoc(doc)) throw new SaveFormatError(`not a ${SAVE_DOC_FORMAT} document, and no legacy upgrade read it`);
      }
      if (doc.game !== spec.game) throw new SaveFormatError(`a save of "${doc.game}", not "${spec.game}"`);
      if (!doc.slices || typeof doc.slices !== "object") throw new SaveFormatError("a save document without slices");
      const migrated: MigrationRun[] = [];
      const dropped: string[] = [];
      const slices: Record<string, SliceData> = {};
      for (const [id, sd] of Object.entries(doc.slices)) {
        const s = spec.slices[id];
        if (!s) {
          dropped.push(id);
          continue;
        }
        const from = Number.isInteger(sd?.v) ? sd.v : FIRST_SLICE_VERSION;
        const data = migrateData(id, from, s.version, sd?.data, s.migrations);
        if (from !== s.version) migrated.push({ slice: id, from, to: s.version });
        slices[id] = { v: s.version, data };
      }
      for (const [id, s] of Object.entries(spec.slices)) if (s.required !== false && !(id in slices)) throw new SaveFormatError(`the save has no "${id}" slice`);
      return { doc: { ...doc, slices, meta: { ...(doc.meta ?? {}) } }, migrated, dropped, legacy };
    },
  };
}

/** The data of every slice in a document, by id. */
export const sliceData = (doc: SaveDoc): Record<string, unknown> => Object.fromEntries(Object.entries(doc.slices).map(([k, s]) => [k, s.data]));
